import { useState, type ReactNode } from 'react'

import type { AgentPriority, PMComponentRef } from '@axilon-apps/domains-ontology-ui-state'

import { ComponentChip } from '../primitives/ComponentChip'
import { TIER_BORDER_CLASSES, TIER_PILL_CLASSES, priorityToTier } from '../primitives/severityTier'
import { cn } from '../../lib/utils'
import { ReviewStateButton } from './ReviewStateButton'

const PRIORITY_LABEL: Record<AgentPriority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low'
}

export interface ActionItemCardProps {
  /** 1-based step index. Renders as a circled number on the leading edge. */
  index: number
  title: string
  description: string
  priority: AgentPriority
  components?: PMComponentRef[]
  /** Whether the operator has marked this action as completed. */
  completed?: boolean
  /** Click handler for Complete. Fired with the next state. */
  onToggleCompleted?: (next: boolean) => void
  /**
   * Click handler for Reject. When set, Deny/Reject is armed here and the
   * reason is typed on the Agent tab — nothing is persisted until that send.
   */
  onToggleReject?: () => void
  /** Lights Reject while a reason is still pending. */
  pendingReject?: boolean
  /** When false, the row still expands; the ▾ glyph is omitted. */
  showChevron?: boolean
  /** Extra controls on the title row. Clicks do not toggle expand. */
  actions?: ReactNode
  /** Revision / pending-reason badge under the title (follow-up chrome). */
  flag?: string | null
  className?: string
}

/**
 * One operator-facing action item rendered as an ordered playbook step.
 * Complete and Reject sit on the title row like Accept/Deny on root causes.
 */
export function ActionItemCard({
  index,
  title,
  description,
  priority,
  components,
  completed,
  onToggleCompleted,
  onToggleReject,
  pendingReject = false,
  showChevron = true,
  actions,
  flag,
  className
}: ActionItemCardProps) {
  const tier = priorityToTier(priority)
  const [expanded, setExpanded] = useState(false)
  const hasDescription = description.trim().length > 0
  const hasDetail = hasDescription || (components?.length ?? 0) > 0
  const hasReview = typeof onToggleCompleted === 'function' || typeof onToggleReject === 'function'
  return (
    <div
      className={cn(
        'flex gap-3 rounded-md border border-line border-l-2 bg-bg-elev p-3 transition-opacity duration-200',
        TIER_BORDER_CLASSES[tier],
        completed && 'opacity-60',
        flag && !completed && 'border-brand',
        className
      )}
    >
      <div
        aria-hidden
        className={cn(
          'mt-0.5 flex size-6.5 shrink-0 items-center justify-center rounded-full border text-xs font-medium',
          completed ? 'border-st-run bg-st-run text-bg-elev' : 'border-line bg-bg-sunk text-ink-2'
        )}
      >
        {completed ? '✓' : index}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex min-h-6 flex-wrap items-center gap-2">
          <button
            type="button"
            data-tour-id={index === 1 ? 'action-item-expand' : undefined}
            onClick={() => hasDetail && setExpanded((v) => !v)}
            aria-expanded={expanded}
            disabled={!hasDetail}
            className={cn(
              'flex min-w-0 flex-1 items-center gap-2 text-left',
              hasDetail && 'cursor-pointer'
            )}
          >
            <span
              className={cn(
                'min-w-0 flex-1 font-medium text-ink',
                completed && 'line-through text-ink-3'
              )}
            >
              {title}
            </span>
            <span
              className={cn(
                'shrink-0 rounded-full px-2 py-px text-10 font-medium uppercase tracking-wide',
                TIER_PILL_CLASSES[tier]
              )}
            >
              {PRIORITY_LABEL[priority]}
            </span>
            {hasDetail && showChevron ? (
              <span
                aria-hidden
                className={cn(
                  'shrink-0 text-ink-3 transition-transform duration-300 ease-out',
                  expanded && 'rotate-180'
                )}
              >
                ▾
              </span>
            ) : null}
          </button>
          {hasReview || actions ? (
            <span className="flex shrink-0 items-center gap-1.5">
              {typeof onToggleCompleted === 'function' ? (
                <ReviewStateButton
                  tone="accept"
                  isActive={Boolean(completed)}
                  isMuted={pendingReject}
                  onClick={() => onToggleCompleted(!completed)}
                >
                  Complete
                </ReviewStateButton>
              ) : null}
              {typeof onToggleReject === 'function' ? (
                <ReviewStateButton
                  tone="deny"
                  isActive={pendingReject}
                  isMuted={Boolean(completed)}
                  onClick={onToggleReject}
                >
                  Reject
                </ReviewStateButton>
              ) : null}
              {actions}
            </span>
          ) : null}
        </div>
        {flag ? (
          <div className="mt-1.5 inline-flex rounded bg-brand-soft px-2 py-0.5 font-mono text-11 font-medium leading-relaxed text-brand">
            {flag}
          </div>
        ) : null}
        {hasDetail ? (
          <div
            data-tour-id={index === 1 ? 'action-item-detail' : undefined}
            className="grid transition-[grid-template-rows] duration-300 ease-out"
            style={{ gridTemplateRows: expanded ? '1fr' : '0fr' }}
          >
            <div className="overflow-hidden">
              {hasDescription ? (
                <p
                  className={cn(
                    'm-0 pt-1.5 text-13 leading-relaxed text-ink-2 whitespace-pre-wrap',
                    completed && 'text-ink-3'
                  )}
                >
                  {description}
                </p>
              ) : null}
              {components && components.length > 0 ? (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {components.map((c) => (
                    <ComponentChip key={`${c.scope}:${c.id}`} component={c} hideScope />
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}
